import {
  Box,
  Button,
  Flex,
  HStack,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useCookieConsentStore } from '@supsupclub/ui-components';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PRIVACY_PATH } from '../../routes';

export const CookieConsentBanner = () => {
  const { t } = useTranslation('common');
  const { consent, acceptAll, rejectAll, setConsent } =
    useCookieConsentStore();
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  if (consent !== null) return null;

  const handleSave = () => {
    setConsent({ analytics: analyticsEnabled });
  };

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={9999}
      bg="white"
      borderTop="1px solid"
      borderColor="gray.200"
      shadow="0 -4px 20px rgba(0,0,0,0.08)"
      p={{ base: 4, md: 6 }}
      animation="slideUp 0.4s ease-out"
    >
      <Box maxW="600px" mx="auto">
        <Stack gap={4}>
          <Text fontSize="sm" color="gray.700">
            {t('cookieConsent.message')}
          </Text>

          <Text fontSize="xs" color="gray.500">
            <Trans i18nKey="cookieConsent.privacyLink" ns="common">
              Learn more in our
              <Link
                to={PRIVACY_PATH}
                style={{ textDecoration: 'underline', fontWeight: 600 }}
              >
                Privacy Policy
              </Link>
            </Trans>
          </Text>

          {showPreferences && (
            <Stack
              gap={3}
              p={4}
              bg="gray.50"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.100"
            >
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="sm" fontWeight="600">
                    {t('cookieConsent.necessary')}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {t('cookieConsent.necessaryDesc')}
                  </Text>
                </Box>
                <Switch.Root checked disabled size="sm">
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
              </Flex>

              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="sm" fontWeight="600">
                    {t('cookieConsent.analytics')}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {t('cookieConsent.analyticsDesc')}
                  </Text>
                </Box>
                <Switch.Root
                  checked={analyticsEnabled}
                  onCheckedChange={(e) => setAnalyticsEnabled(e.checked)}
                  size="sm"
                  colorPalette="purple"
                >
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
              </Flex>

              <Button
                size="sm"
                variant="solid"
                colorPalette="purple"
                onClick={handleSave}
                w="full"
              >
                {t('cookieConsent.save')}
              </Button>
            </Stack>
          )}

          <HStack gap={3} w="full">
            <Button
              size="sm"
              variant="outline"
              flex={1}
              onClick={rejectAll}
            >
              {t('cookieConsent.rejectAll')}
            </Button>
            <Button
              size="sm"
              variant="solid"
              colorPalette="purple"
              flex={1}
              onClick={acceptAll}
            >
              {t('cookieConsent.acceptAll')}
            </Button>
          </HStack>

          {!showPreferences && (
            <Button
              size="xs"
              variant="plain"
              color="gray.500"
              onClick={() => setShowPreferences(true)}
              _hover={{ color: 'gray.700' }}
            >
              {t('cookieConsent.manage')}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
